'use client';

import React, { useEffect, useState } from 'react';
import { Avatar } from 'antd';
import { RightOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';

interface WorkspaceSelectionProps {
  staffingCorpAccessList: {
    staffing_details: Array<{
      total_count: number;
      corporation_ids: number[];
      legal_entity_ids: number[];
      svc_cat_id: number;
      svc_cat_details: {
        corporation_id: number;
        sp_type: string;
        code: string;
        display: string;
        logo_url: string | null;
      };
    }>;
    parent_corp_access: boolean;
    corporation_details: Array<{
      id: number;
      full_name: string;
      logo_url: string | null;
    }>;
    legal_entity_details: Array<{
      id: number;
      name: string;
      logo_url: string | null;
    }>;
  };
  onWorkspaceSelect: (workspaceData: any) => void;
  onBack?: () => void;
}

const WorkspaceSelection: React.FC<WorkspaceSelectionProps> = ({
  staffingCorpAccessList,
  onWorkspaceSelect,
  onBack
}) => {
  const [selectedWorkspace, setSelectedWorkspace] = useState<any>(null);
  const { userData } = useSelector((state: any) => state.auth);
  const userInfo = userData?.userinfo;

  const handleWorkspaceSelect = (workspace: any) => {
    setSelectedWorkspace(workspace);
  };

  const handleContinue = () => {
    if (selectedWorkspace) {
      onWorkspaceSelect(selectedWorkspace);
    }
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && selectedWorkspace) {
        handleContinue();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedWorkspace]);

  return (
    <div className="w-full bg-white rounded-2xl shadow-[0px_0px_8px_rgba(0,0,0,0.03)]">
      <div className="px-6 pt-6 pb-4">
        {onBack && (
          <div className="flex items-center gap-3 mb-4">
            <button onClick={onBack} className="p-2 hover:bg-[#F9FAFB] rounded-lg transition-colors">
              <ArrowLeftOutlined className="text-[#667085]" />
            </button>
            <h1 className="text-[28px] font-semibold text-[#101828]">Workspaces</h1>
          </div>
        )}
        {!onBack && <h1 className="text-[28px] font-semibold text-[#101828] mb-1">Workspaces</h1>}
        <p className="text-base text-[#475467]">Choose a workspace to continue</p>
      </div>

      {userInfo?.parentCorpData && (
        <>
          <div className="px-4">
            <div
              className={`group cursor-pointer rounded-lg transition-all duration-200 hover:bg-[#F9FAFB] ${
                selectedWorkspace?.id === userInfo.parentCorpData.id && selectedWorkspace?.type === 'corporation'
                  ? 'bg-[#F9FAFB]'
                  : ''
              }`}
              onClick={() => handleWorkspaceSelect({ ...userInfo.parentCorpData, type: 'corporation' })}
            >
              <div className="flex items-center justify-between py-2.5 px-2">
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0">
                    <Avatar
                      src={userInfo.parentCorpData.logo_url?.[0]?.presigned_url || '/assets/login/company-default.svg'}
                      size={32}
                      className="bg-[#F4EBFF]"
                      style={{ border: '1px solid #F9F5FF' }}
                    />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[#101828]">{userInfo.parentCorpData.full_name}</p>
                  </div>
                </div>
                <RightOutlined className="text-[#667085]" />
              </div>
            </div>
          </div>

          {staffingCorpAccessList.staffing_details?.length > 0 && (
            <div className="px-6 py-3">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-[#EAECF0]"></div>
                </div>
                <div className="relative flex justify-center">
                  <span className="bg-white px-3 text-sm text-[#667085]">OR</span>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      <div className="px-4 space-y-1">
        {staffingCorpAccessList.staffing_details.map((item) => (
          <div
            key={item.svc_cat_id}
            className={`group cursor-pointer rounded-lg transition-all duration-200 hover:bg-[#F9FAFB] ${
              selectedWorkspace?.svc_cat_id === item.svc_cat_id && selectedWorkspace?.type === 'staffing'
                ? 'bg-[#F9FAFB]'
                : ''
            }`}
            onClick={() => handleWorkspaceSelect({ ...item, type: 'staffing' })}
          >
            <div className="flex items-center justify-between py-2.5 px-2">
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0">
                  <Avatar
                    src={item.svc_cat_details.logo_url || '/assets/login/team-default.svg'}
                    size={32}
                    className="bg-[#F4EBFF]"
                    style={{ border: '1px solid #F9F5FF' }}
                  />
                </div>
                <div>
                  <p className="text-sm font-medium text-[#101828]">{item.svc_cat_details.display}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-[#F4EBFF] text-[#6941C6]">
                  {item.total_count}
                </span>
                <RightOutlined className="text-[#667085]" />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="p-4">
        <button
          onClick={handleContinue}
          disabled={!selectedWorkspace}
          className={`w-full py-2.5 px-4 rounded-lg text-sm font-semibold transition-colors ${
            selectedWorkspace
              ? 'bg-[#7F56D9] text-white hover:bg-[#6941C6]'
              : 'bg-[#F2F4F7] text-[#D0D5DD] cursor-not-allowed'
          }`}
        >
          Proceed to login
        </button>
      </div>
    </div>
  );
};

export default WorkspaceSelection; 